<script>
import {
    modalState, hideModal
} from '@app/store/modalStore.svelte.js';
import {
    projectState
} from '@app/store/projectStore.svelte.js';
import {
    insertHistoryAction
} from '@app/store/historyStore.svelte.js';
import {
    authState
} from '@app/store/authStore.svelte.js';
import { toast } from '@zerodevx/svelte-toast';
import {
    eventGroupState
} from '@app/store/eventGroupStore.svelte.js';
import {
    speakerState
} from '@app/store/speakerStore.svelte.js';
import {
    onDestroy
} from 'svelte';
import {
    fade
} from 'svelte/transition';
import Papa from 'papaparse';
import Swal from "sweetalert2";
import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";
import {
    BarLoader,
    Circle
} from 'svelte-loading-spinners';
import * as m from '@app/paraglide/messages.js';

/* CC LIB */
import _EventGroup from '@cclib/classes/eventGroup.js';
import _Event from '@cclib/classes/event.js';

/* Firebase */
import firebase from '@app/configs/firebase.js';
import 'firebase/compat/functions';
import db from '@app/configs/firestore.js';
import storage from '@app/configs/storage.js';

let jobs = $state([]),
    selectedJob = $state(),
    userId = firebase.auth().currentUser.uid,
    email = firebase.auth().currentUser.email,
    teamId = authState.team ? authState.team.id : null,
    dbRef = authState.team ? db.collection("teams").doc(teamId).collection("jobs") : db.collection("users").doc(userId).collection("jobs"),
    storageRef = storage.ref(),
    storagePath = authState.team ? `teams/${teamId}/jobs/sceneDescriptions/` : `users/${userId}/jobs/sceneDescriptions/`,
    deletingFile = $state(false),
    loading = $state(false),
    autoRefresh = $state(true);

let dateRange = $state("week"),
    startDate = $state(),
    endDate = $state(),
    statusFilter = $state("All"),
    pending = false,
    gettingJobs = $state(getSceneDescriptionJobs());

let autoRefreshInterval = setInterval(function () {
    if (!loading && !pending) {
        gettingJobs = getSceneDescriptionJobs();
    }
}, 10000);

onDestroy(() => {
    if (autoRefreshInterval) {
        clearInterval(autoRefreshInterval);
    }
});

function disableAutoRefresh() {
    clearInterval(autoRefreshInterval);
    autoRefreshInterval = undefined;
    autoRefresh = false;
}

function enableAutoRefresh() {
    if (autoRefreshInterval) {
        clearInterval(autoRefreshInterval);
    }

    autoRefreshInterval = setInterval(function () {
        if (!loading && !pending) {
            gettingJobs = getSceneDescriptionJobs();
        }
    }, 10000);

    autoRefresh = true;
}

function getDateRange() {
    let start, end;
    switch (dateRange) {
        case "custom":
            start = new Date(startDate);
            end = new Date(endDate);
            break;
        case "week":
            start = new Date();
            end = new Date();
            start.setHours(start.getHours() - 168);
            break;
        case "month":
            start = new Date();
            end = new Date();
            start.setMonth(start.getMonth() - 1);
            break;
        case "year":
            start = new Date();
            end = new Date();
            start.setFullYear(start.getFullYear() - 1);
            break;
        default:
            start = new Date();
            end = new Date();
            start.setHours(start.getHours() - 24);
    }

    return { start, end };
}

function getSceneDescriptionJobs() {
    if (pending === true) {
        return;
    }
    pending = true;
    let queryDateRange = getDateRange();

    selectedJob = undefined;
    jobs = [];

    return dbRef.where('createdOn', ">=", queryDateRange.start)
        .where('createdOn', "<=", queryDateRange.end)
        .where('type', "==", "automatic shot descriptions")
        .where('status', "in", statusFilter === 'All' ? ["Submitted", "Processing", "Passed", "Failed", "In Progress"] : [statusFilter])
        .orderBy('createdOn', 'desc')
        .get().then((querySnapshot) => {
            querySnapshot.forEach((doc) => {
                if (!doc.data().archived) {
                    jobs = [...jobs, doc.data()];
                }
            });

            if (allJobsComplete(jobs)) {
                disableAutoRefresh();
            }
        }).catch((error) => {
            console.log("Error getting scene description jobs: ", error);
            toast.push(m.no_scene_description_jobs_found(), {
                classes: ["toast-danger"]
            });
        }).finally(() => {
            pending = false;
        });
}

function allJobsComplete(jobs) {
    let inProgressJob = jobs.findIndex(job => {
        return job.progress !== 100;
    });

    return inProgressJob === -1;
}

async function importSceneDescriptionProject() {
    disableAutoRefresh();
    loading = true;

    try {
        toast.push(m.loading_translation_please_wait(), {
            classes: ["toast-info"]
        });

        // Download the CCPRJ file from storage
        let url = await storageRef
            .child(storagePath + selectedJob.id + "/output/" + selectedJob.id + ".ccprj")
            .getDownloadURL();
        let res = await fetch(url);
        let ccprjData = await res.json();

        // Import event groups from the CCPRJ
        if (ccprjData.eventGroups && ccprjData.eventGroups.length > 0) {
            ccprjData.eventGroups.forEach(evg => {
                let newEvg = { ...(new _EventGroup(evg)) };
                newEvg.selected = [];               
                eventGroupState.push(newEvg);

                // Add speakers from event group level
                if (evg.speakers && evg.speakers.length > 0) {
                    evg.speakers.forEach(sp => {
                        const speakerExists = speakerState.some(speaker => speaker.id === sp.id);
                        if (!speakerExists && sp.id) {
                            speakerState.push({...sp});
                        }
                    });
                }

                // Add speakers from individual events
                if (evg.events && evg.events.length > 0) {
                    evg.events.forEach(event => {
                        if (event.speakers && Array.isArray(event.speakers) && event.speakers.length > 0) {
                            event.speakers.forEach(eventSpeaker => {
                                const speakerExists = speakerState.some(speaker => speaker.id === eventSpeaker.id);
                                if (!speakerExists && eventSpeaker.id) {
                                    speakerState.push({...eventSpeaker});
                                }
                            });
                        }
                    });
                }
            });

            projectState.selected = eventGroupState.length - 1;

            insertHistoryAction({
                name: "import scene description project",
                eventGroup: projectState.selected,
                snapshots: [{
                    store: "eventGroupState",
                    value: JSON.stringify(eventGroupState)
                }]
            });

            toast.push(m.scene_description_import_success(), {
                classes: ["toast-success"]
            });
        }

        hideModal();
    } catch (err) {
        console.error(err);
        loading = false;
        toast.push(m.scene_description_import_failed({ error: err.message }), {
            classes: ["toast-danger"]
        });
    }
}

function archiveJob() {
    deletingFile = true;
    dbRef.doc(selectedJob.id).update({
        archived: true
    }).then(() => {
        toast.push(m.scene_description_job_archived_success(), {
            classes: ["toast-success"]
        });
        gettingJobs = getSceneDescriptionJobs();
    }).catch((e) => {
        toast.push(m.error_archiving_job({ error: e.message }), {
            classes: ["toast-danger"]
        });
        console.log(e, e.message);
    }).finally(() => {
        deletingFile = false;
    });
}

async function deleteSceneDescriptionJob() {
    deletingFile = true;

    let swalRes = await Swal.fire({
        ...SWAL_BASE,
        customClass: SWAL_CLASSES.destructive,
        title: m.delete_scene_description_job_title(),
        text: m.delete_scene_description_job_text({ project: selectedJob.projectName }),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: m.yes(),
        cancelButtonText: m.cancel()
    });

    if (!swalRes.isConfirmed) {
        deletingFile = false;
        return;
    }

    try {
        await dbRef.doc(selectedJob.id).update({
            deleted: true,
            status: "Deleted",
            updatedOn: firebase.firestore.Timestamp.fromDate(new Date())
        });

        toast.push(m.scene_description_job_deleted_success(), {
            classes: ["toast-success"]
        });
        gettingJobs = getSceneDescriptionJobs();
    } catch (e) {
        toast.push(m.error_deleting_translation_file({ error: e.message }), {
            classes: ["toast-danger"]
        });
        console.log(e, e.message);
    } finally {
        deletingFile = false;
    }
}

function exportCsv() {
    toast.push(m.exporting_results(), {
        classes: ["toast-info"]
    });

    let csv = Papa.unparse(
        JSON.stringify(
            jobs.map(job => {
                return {
                    id: job.id,
                    projectId: job.projectId,
                    projectName: job.projectName,
                    progress: job.progress,
                    createdOn: job.createdOn.toDate(),
                    completedOn: job.completedOn ? job.completedOn.toDate() : '',
                    updatedOn: job.updatedOn.toDate(),
                    status: job.status,
                    statusMsg: job.statusMsg,
                    currentStep: job.currentStep || '',
                    type: job.type,
                    duration: job.duration,
                    cost: job.cost,
                    transcriptionProvider: job.config.transcriptionProvider || "",
                    llmProvider: job.config.llmProvider || "",
                    language: job.config.language || ""
                }
            })
        )
    );

    let fileBlob = new Blob([csv], {
        type: "text/csv;charset=utf-8"
    });

    saveAs(fileBlob, "report_automatic_shot_description_jobs.csv", {
        autoBom: true
    });
}
</script>

<div transition:fade={{ duration: 100 }} class="modal {modalState.selected === 'aiSceneDescriptionImport' ? 'show d-block' : ''}" role="dialog" tabindex="-1" id="AiSceneDescriptionImportModal" aria-labelledby="aiSceneDescriptionImportTitle">
    <div class="modal-dialog modal-xl modal-dialog-centered" role="document">
        <div class="modal-content">
            <div class="modal-header">
                <h4 id="aiSceneDescriptionImportTitle" class="modal-title">{m.scene_description_import_dashboard_title()}</h4>
                <button type="button" class="btn-close" aria-label={m.close()} onclick={hideModal} id="closeAiSceneDescriptionImport"></button>
            </div>
            <div class="modal-body">
                <div class="btn-group float-end mb-2" role="group">
                    <button type="button" class="btn btn-outline-dark" onclick={exportCsv} disabled={jobs.length === 0}>{m.export_as_csv()}</button>
                </div>
                <!-- Search Form -->
                <form>
                    <div class="row g-1">
                        <div class="col-2">
                            <select class="form-select me-2 mb-2" bind:value={dateRange}>
                                <option value="today">{m.past_24_hours()}</option>
                                <option value="week">{m.past_week()}</option>
                                <option value="month">{m.past_month()}</option>
                                <option value="year">{m.past_year()}</option>
                                <option value="custom">{m.custom()}</option>
                            </select>
                        </div>
                        {#if dateRange === 'custom'}
                        <div class="col-2">
                            <input class="form-control me-2 mb-2" type="date" bind:value={startDate} />
                        </div>
                        <div class="col-2">
                            <input class="form-control me-2 mb-2" type="date" bind:value={endDate} />
                        </div>
                        {/if}
                        <div class="col-2">
                            <select class="form-select me-2 mb-2" bind:value={statusFilter}>
                                <option value="All">{m.all()}</option>
                                <option value="Submitted">{m.submitted()}</option>
                                <option value="Processing">{m.in_progress()}</option>
                                <option value="Passed">{m.passed()}</option>
                                <option value="Failed">{m.failed()}</option>
                            </select>
                        </div>
                        <div class="col">
                            <div class="btn-group" role="group">
                                <button type="button" class="btn btn-light" disabled={dateRange === 'custom' && (!startDate || !endDate)} onclick={() => { gettingJobs = getSceneDescriptionJobs() }}>{m.refresh()}</button>
                                <button type="button" class="btn {autoRefresh ? 'btn-primary' : 'btn-light'}" onclick={() => { autoRefresh ? disableAutoRefresh() : enableAutoRefresh() }}><i class="bi {autoRefresh ? 'bi-check-circle-fill' : 'bi bi-circle'}"></i> {m.auto_refresh()}</button>
                            </div>
                        </div>
                        {#if dateRange === 'custom' && (!startDate || !endDate)}
                        <p class="text-danger small pt-2 me-2">{m.custom_date_range_required()}</p>
                        {/if}
                        {#if loading}
                        <div class="col">
                            <Circle size="30" color="#1eb4b2" unit="px" duration="1s"></Circle>
                        </div>
                        {/if}
                    </div>
                </form>
                <!-- Results Table -->
                <div id="SceneDescriptionJobTableDashboard" class="table-responsive rounded shadow border border-secondary">
                    <table class="table table-hover table-sm">
                        <thead class="bg-dark text-dark">
                            <tr>
                                <th class="text-truncate">{m.project_name()}</th>
                                <th class="text-truncate">{m.progress()}</th>
                                <th>{m.status()}</th>
                                <th class="text-truncate">{m.current_step()}</th>
                                <th class="text-truncate">{m.status_message()}</th>
                                <th>{m.provider()}</th>
                                <th class="text-truncate">{m.llm_provider()}</th>
                                <th class="text-truncate">{m.source_language()}</th>
                                <th class="w-25">{m.job_id()}</th>
                                <th class="text-truncate">{m.submitted_by()}</th>
                                <th>{m.created_on()}</th>
                                <th>{m.completed_on()}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {#await gettingJobs}
                            <tr>
                                <td colspan="12">
                                    <div style="padding: 10%; position:relative; left:105%;">
                                        <BarLoader size="160" color="#1eb4b2" unit="px" duration="3s"></BarLoader>
                                    </div>
                                </td>
                            </tr>
                            {:then}
                            {#each jobs as job (job.id)}
                            <tr onclick={() => { selectedJob = job }} class={selectedJob && job.id === selectedJob.id ? 'table-warning' : ''}>
                                <td class="text-truncate" title={job.projectName}>{job.projectName}</td>
                                <td class="align-middle">
                                    <div class="progress" style="width:150px">
                                        <div class="progress-bar {job.progress < 100 ? 'progress-bar-animated' : ''} {job.status === 'Failed' ? 'bg-danger' : ''} {job.status === 'Passed' ? 'bg-primary' : ''} progress-bar-striped" role="progressbar" aria-valuemin="0" aria-valuemax="100" style="width: {job.progress}%">{job.progress}%</div>
                                    </div>
                                </td>
                                <td title={job.status} class="text-truncate {job.status === 'Passed' ? 'text-primary' : ''} {job.status === 'Failed' ? 'text-danger' : ''}">{job.status}</td>
                                <td class="text-truncate" title={job.currentStep || ''}>{job.currentStep || ''}</td>
                                <td title={job.statusMsg} class='text-truncate'>{job.statusMsg}</td>
                                <td class="text-truncate" title={job.config.transcriptionProvider}>{job.config.transcriptionProvider}</td>
                                <td class="text-truncate" title={job.config.llmProvider}>{job.config.llmProvider}</td>
                                <td title={job.config.language}>{job.config.language}</td>
                                <td class="text-truncate small" title={job.id}>{job.id}</td>
                                <td class="text-truncate" title={job.submittedBy || email}>{job.submittedBy || email}</td>
                                <td class="text-truncate" title={job.createdOn.toDate()}>{job.createdOn.toDate().toString().substring(0, 24)}</td>
                                <td class="text-truncate" title={job.completedOn ? job.completedOn.toDate() : ""}>{job.completedOn ? job.completedOn.toDate().toString().substring(0, 24) : ""}</td>
                            </tr>
                            {:else}
                            <tr>
                                <td colspan="12" class="text-light text-center m-5 text-truncate">{m.no_scene_description_jobs_found()}</td>
                            </tr>
                            {/each}
                            {/await}
                        </tbody>
                    </table>
                </div>
                {#if selectedJob}
                    <div class="btn-group float-end" role="group">
                        <button type="button" class="btn btn-primary" onclick={importSceneDescriptionProject} disabled={loading || deletingFile || selectedJob.deleted || selectedJob.status !== 'Passed'}>
                            <i class="bi bi-box-arrow-in-down"></i> {m.import_scene_description_ccprj()}
                        </button>
                    </div>
                    <div class="btn-group" role="group">
                        {#if selectedJob.status !== "Passed"}
                        <button title={m.archive_job()} type="button" class="btn btn-info text-white" disabled={deletingFile} onclick={archiveJob}><i class="bi bi-archive"></i> {m.archive_job()}</button>
                        {/if}
                        {#if !selectedJob.deleted && selectedJob.status === 'Passed'}
                        <button type="button" class="btn btn-danger text-white" disabled={deletingFile} onclick={deleteSceneDescriptionJob}><i class="bi bi-trash"></i> {m.delete()}</button>
                        {/if}
                    </div>
                {/if}
            </div>
        </div>
    </div>
</div>

<style lang="css">
#SceneDescriptionJobTableDashboard {
    max-height: 500px;
    margin-bottom: 2%;
}
</style>
